import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const BackgroundJobsContext = createContext(null);

const STORAGE_KEY = 'ariaone_bg_jobs';
const POLL_INTERVAL = 2000;

const loadJobs = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
  catch { return []; }
};

const saveJobs = (jobs) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs)); } catch {}
};

export const BackgroundJobsProvider = ({ children, authFetch }) => {
  const [jobs, setJobs] = useState(() => loadJobs());
  const pollRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL || '';

  // Persist to localStorage on change
  useEffect(() => { saveJobs(jobs); }, [jobs]);

  const addJob = useCallback((job) => {
    setJobs(prev => {
      const filtered = prev.filter(j => j.job_id !== job.job_id);
      return [{ ...job, added_at: new Date().toISOString() }, ...filtered];
    });
  }, []);

  const updateJob = useCallback((jobId, updates) => {
    setJobs(prev => prev.map(j => j.job_id === jobId ? { ...j, ...updates } : j));
  }, []);

  const dismissJob = useCallback((jobId) => {
    setJobs(prev => prev.filter(j => j.job_id !== jobId));
  }, []);

  const getJob = useCallback((jobId) => {
    return jobs.find(j => j.job_id === jobId) || null;
  }, [jobs]);

  // Poll active jobs
  useEffect(() => {
    const activeJobs = jobs.filter(j => j.status === 'pending' || j.status === 'processing');
    if (activeJobs.length === 0) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }

    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      for (const job of activeJobs) {
        if (!job.poll_url || !authFetch) continue;
        try {
          const res = await authFetch(`${API}${job.poll_url}`);
          if (!res.ok) continue;
          const data = await res.json();
          if (!data || !data.status) continue;

          updateJob(job.job_id, data);

          if (data.status === 'completed') {
            const label = job.label || 'Import';
            if (data.imported > 0 && data.errors === 0) {
              toast.success(`${label}: ${data.imported} imported`);
            } else if (data.imported === 0 && data.duplicates > 0 && data.errors === 0) {
              toast.info(`${label}: All ${data.duplicates} already exist`);
            } else {
              toast.warning(`${label}: ${data.imported || 0} imported, ${data.duplicates || 0} dups, ${data.errors || 0} errors`);
            }
          } else if (data.status === 'failed') {
            toast.error(`${job.label || 'Import'} failed: ${data.error_detail || 'Unknown error'}`);
          }
        } catch {}
      }
    }, POLL_INTERVAL);

    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [jobs, authFetch, API, updateJob]);

  const activeCount = jobs.filter(j => j.status === 'pending' || j.status === 'processing').length;

  return (
    <BackgroundJobsContext.Provider value={{ jobs, activeCount, addJob, updateJob, dismissJob, getJob }}>
      {children}
    </BackgroundJobsContext.Provider>
  );
};

export const useBackgroundJobs = () => {
  const ctx = useContext(BackgroundJobsContext);
  if (!ctx) throw new Error('useBackgroundJobs must be used within BackgroundJobsProvider');
  return ctx;
};
